// -------------------------------
// 🔹 Imports externes (librairies tierces)
// -------------------------------
import { useMutation } from '@tanstack/vue-query';
import type { AxiosError } from 'axios';

// -------------------------------
// 🔹 Imports internes — Références réactives (refs globales)
// -------------------------------
import { isGlobalError, GlobalErrorMessage } from '@/refs/common.refs';
import { isLoaderLoginFormActivated } from '@/refs/login.form.refs';

// -------------------------------
// 🔹 Imports internes — Stores Pinia
// -------------------------------
import { useCommerceStore } from '@/store/commerce.store';
import { useCategoryStore } from '@/store/category.store';
import { useDialogStore } from '@/store/dialog.store';
import { useUserStore } from '@/store/user.store';

// -------------------------------
// 🔹 Imports internes — Types TypeScript
// -------------------------------
import type { ICategoryBody } from '@/types/category.types';
import { idToDelete } from '@/refs/commerce.refs';
import type {
  IAssignmentsResponse,
  IBodyUser,
  IBodyUserCustomer,
  IBodyUserInfo,
  IBodyUserUpdatePassword,
  IRemovalsResponse,
  IRoleBody,
  IUpdateStaffRole,
} from '@/types/user.type';
import { UserService } from '@/services/user.service';

/**
 * Hook pour gérer les mutations liées aux users.
 * Fournit des fonctions pour créer, mettre à jour et supprimer des users
 * avec gestion automatique des erreurs globales et du loader.
 */
export const UserMutation = () => {
  const commerceStore = useCommerceStore();
  const dialogStore = useDialogStore();
  const categorieStore = useCategoryStore();
  const userStore = useUserStore();

  // -------------------------------
  // 🔹 Gestion centralisée des erreurs
  // -------------------------------
  const handleError = (err: any) => {
    const error = err as AxiosError;
    const response = error.response?.data as any;
    const message = response?.message?.[0] || 'Une erreur est survenue';

    isGlobalError.value = true;
    GlobalErrorMessage.value = message;
    setTimeout(() => {
      isGlobalError.value = false;
      isLoaderLoginFormActivated.value = false;
    }, 1000);
  };

  // -------------------------------
  // 🔹 Création d'un user
  // -------------------------------
  const createUser = useMutation<any, Error, IBodyUser>({
    mutationFn: (payload: IBodyUser) => {
      return userStore.createUser(payload);
    },
    onSuccess: async (data) => {},
    onError: handleError,
  });

  // -------------------------------
  // 🔹 Création d'un user custommer
  // -------------------------------
  const createUserCustomer = useMutation<any, Error, IBodyUserCustomer>({
    mutationFn: (payload: IBodyUserCustomer) => {
      return userStore.createUserCustomer(payload);
    },
    onSuccess: async (data) => {},
    onError: handleError,
  });

  // -------------------------------
  // 🔹 Mise à jour d'un user custommer par son Id
  // -------------------------------
  const updateUserCustomerById = useMutation<any, Error, IBodyUserCustomer>({
    mutationFn: (payload: IBodyUserCustomer) => {
      return userStore.updateUserCustomerById(payload);
    },
    onSuccess: async (data) => {},
    onError: handleError,
  });

  // -------------------------------
  // 🔹 Suppression d'un client
  // -------------------------------
  const DeleteUserClientCustomer = useMutation<any, Error>({
    mutationFn: () => UserService.DeleteUserCustomer(),
    onSuccess: () => {},
    onError: handleError,
  });

  // -------------------------------
  // 🔹 Création d'un user staff role
  // -------------------------------
  const createUserRoleStaff = useMutation<any, Error, IRoleBody>({
    mutationFn: (payload: IRoleBody) => {
      return userStore.createUserRoleStaff(payload);
    },
    onSuccess: async (data) => {},
    onError: handleError,
  });

  // -------------------------------
  // 🔹 Mise à jour d'un user staff role par son Id
  // -------------------------------
  const updateUserStaffRole = useMutation<any, Error, IUpdateStaffRole>({
    mutationFn: (payload: IUpdateStaffRole) => {
      return userStore.updateUserStaffRole(payload);
    },
    onSuccess: async (data) => {},
    onError: handleError,
  });

  // -------------------------------
  // 🔹 Assigner un role à l'utilisateur
  // -------------------------------
  const assignUserRoles = useMutation<any, Error, IAssignmentsResponse>({
    mutationFn: (payload: IAssignmentsResponse) => {
      return userStore.assignUserRoles(payload);
    },
    onSuccess: async (data) => {},
    onError: handleError,
  });

  // -------------------------------
  // 🔹 Suppression d'un staff role
  // -------------------------------
  const DeleteUserStaffRole = useMutation<any, Error>({
    mutationFn: () => UserService.DeleteUserStaffRole(),
    onSuccess: () => {},
    onError: handleError,
  });
  // -------------------------------
  // 🔹 Desassigner le role de l'utilisatier
  // -------------------------------
  const removeUserRoles = useMutation<any, Error, IRemovalsResponse>({
    mutationFn: (payload: IRemovalsResponse) =>
      UserService.removeUserRoles(payload),
    onSuccess: () => {},
    onError: handleError,
  });

  // -------------------------------
  // 🔹 Mise à jour d'un user staff role par son Id
  // -------------------------------
  const updateMyPassword = useMutation<any, Error, IBodyUserUpdatePassword>({
    mutationFn: (payload: IBodyUserUpdatePassword) => {
      return userStore.updateMyPassword(payload);
    },
    onSuccess: async (data) => {},
    onError: handleError,
  });

  // -------------------------------
  // 🔹 Mise à jour d'un user staff role par son Id
  // -------------------------------
  const updateUserPassword = useMutation<any, Error, IBodyUserUpdatePassword>({
    mutationFn: (payload: IBodyUserUpdatePassword) => {
      return userStore.updateUserPassword(payload);
    },
    onSuccess: async (data) => {},
    onError: handleError,
  });

  // -------------------------------
  // 🔹 Mise à jour d'un user staff role par son Id
  // -------------------------------
  const updateUserInfo = useMutation<any, Error, IBodyUserInfo>({
    mutationFn: (payload: IBodyUserInfo) => {
      return userStore.updateUserInfo(payload);
    },
    onSuccess: async (data) => {},
    onError: handleError,
  });

  // -------------------------------
  // 🔹 Mise à jour d'un user staff role par son Id
  // -------------------------------
  const updateMyInfo = useMutation<any, Error, IBodyUserInfo>({
    mutationFn: (payload: IBodyUserInfo) => {
      return userStore.updateMyInfo(payload);
    },
    onSuccess: async (data) => {},
    onError: handleError,
  });
  // 🔹 Mise à jour d'un user staff role par son Id
  // -------------------------------
  const deleteUser = useMutation<any, Error>({
    mutationFn: () => {
      return userStore.deleteUser();
    },
    onSuccess: async (data) => {},
    onError: handleError,
  });

  // -------------------------------
  // 🔹 Fonctions exposées pour les composants
  // -------------------------------
  /**
   * Crée une nouvelle user
   * @param {IBodyUser} item - Données de l'utilisateur
   */
  const handleCreateUser = (item: IBodyUser) => {
    return createUser.mutateAsync(item);
  };

  /**
   * Crée un nouvel utilisateur customer.
   * @param {IBodyUserCustomer} item - Données de l'utilisateur customer.
   * @returns {Promise<any>} Promesse résolue lorsque l'utilisateur est créé.
   */
  const handleCreateUserCustomer = (item: IBodyUserCustomer) => {
    return createUserCustomer.mutateAsync(item);
  };

  /**
   * Met à jour un utilisateur customer existant.
   * @param {IBodyUserCustomer} item - Données de mise à jour de l'utilisateur customer.
   * @returns {Promise<any>} Promesse résolue lorsque la mise à jour est effectuée.
   */
  const handleUpdateUserCustomerById = (item: IBodyUserCustomer) => {
    return updateUserCustomerById.mutateAsync(item);
  };

  /**
   * Supprime un utilisateur customer existant.
   * @returns {Promise<any>} Promesse résolue lorsque la suppression est effectuée.
   */
  const handleDeleteUserClientCustomer = () => {
    return DeleteUserClientCustomer.mutateAsync();
  };

  /**
   * Crée un nouveau staff role pour un utilisateur.
   * @param {IRoleBody} item - Données du staff role.
   * @returns {Promise<any>} Promesse résolue lorsque le rôle est créé.
   */
  const handleCreateUserRoleStaff = (item: IRoleBody) => {
    return createUserRoleStaff.mutateAsync(item);
  };

  /**
   * Met à jour un staff role existant pour un utilisateur.
   * @param {IUpdateStaffRole} item - Données de mise à jour du staff role.
   * @returns {Promise<any>} Promesse résolue lorsque la mise à jour est effectuée.
   */
  const handleUpdateUserStaffRole = (item: IUpdateStaffRole) => {
    return updateUserStaffRole.mutateAsync(item);
  };

  /**
   * Supprime un staff role existant pour un utilisateur.
   * @returns {Promise<any>} Promesse résolue lorsque la suppression est effectuée.
   */
  const handleDeleteUserStaffRole = () => {
    return DeleteUserStaffRole.mutateAsync();
  };

  /**
   * Assigne un ou plusieurs rôles à un utilisateur.
   * @param {IAssignmentsResponse} item - Données des rôles à assigner.
   * @returns {Promise<any>} Promesse résolue lorsque les rôles sont assignés.
   */
  const assignUserRole = (item: IAssignmentsResponse) => {
    return assignUserRoles.mutateAsync(item);
  };

  /**
   * Retire un ou plusieurs rôles d'un utilisateur.
   * @param {IRemovalsResponse} item - Données des rôles à retirer.
   * @returns {Promise<any>} Promesse résolue lorsque les rôles sont retirés.
   */
  const removeUserRole = (item: IRemovalsResponse) => {
    return removeUserRoles.mutateAsync(item);
  };

  /**
   * Met à jour le mot de passe de l'utilisateur courant.
   * @param {IBodyUserUpdatePassword} item - Données pour la mise à jour du mot de passe.
   * @returns {Promise<any>} Promesse résolue lorsque la mise à jour est effectuée.
   */
  const updateMyPass = (item: IBodyUserUpdatePassword) => {
    return updateMyPassword.mutateAsync(item);
  };

  /**
   * Met à jour le mot de passe d'un utilisateur spécifique.
   * @param {IBodyUserUpdatePassword} item - Données pour la mise à jour du mot de passe.
   * @returns {Promise<any>} Promesse résolue lorsque la mise à jour est effectuée.
   */
  const updateUserPasswords = (item: IBodyUserUpdatePassword) => {
    return updateUserPassword.mutateAsync(item);
  };

  /**
   * Met à jour les informations d'un utilisateur spécifique.
   * @param {IBodyUserInfo} item - Données pour la mise à jour des informations utilisateur.
   * @returns {Promise<any>} Promesse résolue lorsque la mise à jour est effectuée.
   */
  const updateUserInfos = (item: IBodyUserInfo) => {
    return updateUserInfo.mutateAsync(item);
  };

  /**
   * Met à jour les informations de l'utilisateur courant.
   * @param {IBodyUserInfo} item - Données pour la mise à jour des informations utilisateur.
   * @returns {Promise<any>} Promesse résolue lorsque la mise à jour est effectuée.
   */
  const updateMyInfos = (item: IBodyUserInfo) => {
    return updateMyInfo.mutateAsync(item);
  };

  /**
   * Supprime un utilisateur existant.
   *
   * Cette fonction utilise `deleteUser.mutateAsync()` pour envoyer
   * une requête de suppression vers le service distant.
   *
   * @returns {Promise<any>} Promesse résolue lorsque l'utilisateur est supprimé avec succès.
   * @throws {AxiosError} Lance une erreur si la suppression échoue.
   */
  const handleDeleteUser = () => {
    return deleteUser.mutateAsync();
  };

  return {
    assignUserRole,
    removeUserRole,
    updateMyPass,
    updateUserPasswords,
    updateUserInfos,
    updateMyInfos,
    handleCreateUser,
    handleCreateUserCustomer,
    handleUpdateUserCustomerById,
    handleDeleteUserClientCustomer,
    handleCreateUserRoleStaff,
    handleUpdateUserStaffRole,
    handleDeleteUserStaffRole,
    handleDeleteUser,
  };
};
